/**
 * «Мессенджер»: вкладки `?tab=accounts` (номера и аккаунты, спека
 * `docs/specs/2026-09-25-messenger-accounts-design.md`) и `?tab=actions`
 * (`MessengerActions.tsx`). Числа — те же, что у `prokat-runner messenger-*`.
 * `?tab=chats` — `MessengerChats.tsx`, карточка чата — `MessengerChatPage.tsx`.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useSearchParams } from "react-router";

import {
  type Account,
  type Actions,
  type Column,
  dt,
  loadMessengerAccounts,
  type Messages,
  type MessengerAccounts,
  rhythm,
  sum,
} from "@/lib/messenger";
import { MessengerActions } from "@/pages/MessengerActions";
import { MessengerChats } from "@/pages/MessengerChats";

const TABS = [
  { key: "accounts", label: "Аккаунты и номера" },
  { key: "actions", label: "Действия" },
  { key: "chats", label: "Чаты" },
] as const;
type Tab = (typeof TABS)[number]["key"];

const COLUMNS: { key: Column; label: string }[] = [
  { key: "wa", label: "WhatsApp" },
  { key: "tg", label: "Telegram" },
  { key: "max", label: "MAX" },
  { key: "sms", label: "SMS" },
];

const yesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toLocaleDateString("sv-SE");
};

/** Сдвиг даты ГГГГ-ММ-ДД на `days` суток; полдень — чтобы переход на летнее время не съел день. */
function shift(day: string, days: number): string {
  const d = new Date(`${day}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("sv-SE");
}

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
const th = "px-3 py-1.5 font-medium";
const num = "px-3 py-1.5 text-right tabular-nums";
const btn = "grid h-8 w-8 place-items-center rounded-[var(--radius-ui)] border border-border hover:bg-bg-hover";

function ActionCounts({ a }: { a: Actions }) {
  return (
    <span title="действия: отправка / новое / архив" className="tabular-nums">
      {a.send}/{a.new}/{a.archive}
      {a.other ? <span className="text-fg-faint">+{a.other}</span> : null}
    </span>
  );
}

function Counts({ a, m }: { a: Actions; m: Messages }) {
  return (
    <span className="tabular-nums">
      <ActionCounts a={a} />
      <span className="text-fg-faint"> · </span>
      <span title="сообщения: входящие / исходящие / рассылка">
        {m.in}/{m.out}
        {m.sender ? <span className="text-fg-faint">/{m.sender}</span> : null}
      </span>
    </span>
  );
}

function AccountCell({ a }: { a: Account }) {
  const busy = sum(a.actions) + sum(a.messages) > 0;
  return (
    <div className="whitespace-nowrap" data-annot={`аккаунт ${a.name}`} title={a.name}>
      <span className={busy ? "" : "text-fg-muted"}>{a.short || a.name}</span>{" "}
      {busy ? (
        <Counts a={a.actions} m={a.messages} />
      ) : (
        <span className="text-fg-faint">{a.last_at ? dt(a.last_at) : "> 90 дн"}</span>
      )}
    </div>
  );
}

function Systems({ r }: { r: MessengerAccounts }) {
  return (
    <section className={card} data-annot="сводка по системам">
      <h2 className="border-b border-border px-4 py-3 text-[13px] font-medium">
        Системы · действий {r.totals.actions} · сообщений {r.totals.messages}
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className={th}>Система</th>
              <th className={`${th} text-right`}>Аккаунтов</th>
              <th className={`${th} text-right`}>Отпр.</th>
              <th className={`${th} text-right`}>Новое</th>
              <th className={`${th} text-right`}>Архив</th>
              <th className={`${th} text-right`}>Вх.</th>
              <th className={`${th} text-right`}>Исх.</th>
              <th className={`${th} text-right`}>Рассылка</th>
              <th className={th}>Последняя активность</th>
            </tr>
          </thead>
          <tbody>
            {r.systems.map((s) => (
              <tr
                key={s.type_id}
                className={`border-t border-border ${s.silent ? "bg-err/10" : ""}`}
                data-annot={`система ${s.name}`}
              >
                <td className={`px-3 py-1.5 ${s.silent ? "font-medium text-err" : ""}`}>{s.name}</td>
                <td className={num}>
                  {s.accounts_active}
                  <span className="text-fg-faint">/{s.accounts}</span>
                </td>
                <td className={num}>{s.actions.send}</td>
                <td className={num}>{s.actions.new}</td>
                <td className={num}>{s.actions.archive}</td>
                <td className={num}>{s.messages.in}</td>
                <td className={num}>{s.messages.out}</td>
                <td className={num}>{s.messages.sender}</td>
                <td className={`px-3 py-1.5 ${s.silent ? "text-err" : s.last_at ? "" : "text-fg-faint"}`}>
                  {s.last_at
                    ? s.silent
                      ? `нет данных с ${dt(s.last_at)}`
                      : s.regular
                        ? dt(s.last_at)
                        : `${dt(s.last_at)} · ${rhythm(s)}`
                    : "> 90 дн"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[11.5px] text-fg-faint">
        Действия — записи CRM без дедупа (отправки без дублей — во вкладке «Действия»). Робот не считается. Красным —
        регулярная система (от 20 дней из 30), которая молчит больше суток: сломан сбор в CRM. Редкие — серым «редко
        N/30».
      </div>
    </section>
  );
}

function Phones({ r }: { r: MessengerAccounts }) {
  return (
    <section className={card} data-annot="номера">
      <h2 className="border-b border-border px-4 py-3 text-[13px] font-medium">Номера: {r.phones.length}</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className={th}>Номер</th>
              <th className={th}>Группа</th>
              <th className={th}>Статус</th>
              <th className={th}>Кто работал</th>
              {COLUMNS.map((c) => (
                <th key={c.key} className={th}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {r.phones.map((p) => (
              <tr
                key={p.id}
                className={`border-t border-border align-top hover:bg-bg-hover ${p.active ? "" : "opacity-60"}`}
                data-annot={`номер ${p.phone}`}
              >
                <td className="whitespace-nowrap px-3 py-1.5">
                  <div className="tabular-nums">{p.phone}</div>
                  <div className="text-[11.5px] text-fg-faint">{p.name}</div>
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 text-fg-muted">{p.group}</td>
                <td className="whitespace-nowrap px-3 py-1.5">
                  {/* Цвет CRM (#006600 и т.п.) текстом не читается на тёмной теме — только точкой. */}
                  {p.status ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="inline-block h-2 w-2 rounded-full ring-1 ring-fg-faint/40"
                        style={{ background: p.status.color ?? "transparent" }}
                      />
                      {p.status.name}
                    </span>
                  ) : (
                    <span className="text-fg-faint">—</span>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  {p.users.length ? p.users.map((u) => u.name).join(", ") : <span className="text-fg-faint">—</span>}
                  {p.work_user ? (
                    <div className="text-[11.5px] text-fg-faint">закреплён: {p.work_user.name}</div>
                  ) : null}
                </td>
                {COLUMNS.map((c) => (
                  <td key={c.key} className="px-3 py-1.5">
                    {p.accounts[c.key].length ? (
                      p.accounts[c.key].map((a) => <AccountCell key={a.id} a={a} />)
                    ) : (
                      <span className="text-fg-faint">—</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[11.5px] text-fg-faint">
        В ячейке: отправка/новое/архив · входящие/исходящие/рассылка. Серым — за день пусто, дата последней активности.
      </div>
    </section>
  );
}

function Loose({ r }: { r: MessengerAccounts }) {
  return (
    <section className={card} data-annot="аккаунты без номера">
      <h2 className="border-b border-border px-4 py-3 text-[13px] font-medium">
        Аккаунты без номера: {r.accounts.length}
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className={th}>Аккаунт</th>
              <th className={th}>Группа</th>
              <th className={th}>Сотрудник</th>
              <th className={th}>За день</th>
            </tr>
          </thead>
          <tbody>
            {r.unbound.map((u) => (
              <tr
                key={`u-${u.source}`}
                className="border-t border-border bg-warn/10"
                data-annot={`без аккаунта ${u.source}`}
              >
                <td className="px-3 py-1.5 text-warn">без аккаунта · {u.source ?? "?"}</td>
                <td className="px-3 py-1.5 text-fg-muted" colSpan={2}>
                  действия с пустым messengerAccountID
                </td>
                <td className="px-3 py-1.5">
                  <ActionCounts a={u.actions} />
                </td>
              </tr>
            ))}
            {r.accounts.map((a) => (
              <tr key={a.id} className="border-t border-border hover:bg-bg-hover">
                <td className="px-3 py-1.5">{a.name}</td>
                <td className="px-3 py-1.5 text-fg-muted">{a.group}</td>
                <td className="px-3 py-1.5">{a.for_user?.name ?? <span className="text-fg-faint">—</span>}</td>
                <td className="px-3 py-1.5">
                  <AccountCell a={a} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function MessengerPage() {
  const [params, setParams] = useSearchParams();
  const day = params.get("date") || yesterday();
  const all = params.get("all") === "1";
  const tab: Tab = TABS.find((t) => t.key === params.get("tab"))?.key ?? "accounts";
  const set = (next: { date?: string; all?: boolean; tab?: Tab }) =>
    setParams(
      { tab: next.tab ?? tab, date: next.date ?? day, all: (next.all ?? all) ? "1" : "0" },
      { replace: true },
    );
  const q = useQuery({
    queryKey: ["messenger-accounts", day, all],
    queryFn: () => loadMessengerAccounts(day, all),
    enabled: tab === "accounts",
  });
  const r = q.data;

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-3" data-annot="шапка мессенджера">
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">Мессенджер</h1>
        <div className="flex gap-1" data-annot="вкладки мессенджера">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => set({ tab: t.key })}
              className={`rounded-[var(--radius-ui)] border border-border px-2.5 py-1 text-[12.5px] ${
                tab === t.key ? "bg-bg-hover text-fg" : "text-fg-muted hover:bg-bg-hover"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === "accounts" ? (
          <label className="flex items-center gap-1.5 text-[12.5px] text-fg-muted">
            <input type="checkbox" checked={all} onChange={(e) => set({ all: e.target.checked })} />и неактивные
            номера
          </label>
        ) : null}
        <div className="ml-auto flex items-center gap-1 text-[12.5px]" data-annot="выбор дня">
          <button type="button" onClick={() => set({ date: shift(day, -1) })} className={btn} title="Предыдущий день">
            <ChevronLeft size={14} />
          </button>
          <input
            type="date"
            value={day}
            onChange={(e) => e.target.value && set({ date: e.target.value })}
            className="h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-fg"
          />
          <button type="button" onClick={() => set({ date: shift(day, 1) })} className={btn} title="Следующий день">
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
      {tab === "actions" ? (
        <MessengerActions day={day} />
      ) : tab === "chats" ? (
        <MessengerChats day={day} />
      ) : q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Читаю мессенджеры из CRM…</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить данные: {String(q.error.message)}</div>
      ) : (
        <>
          <Systems r={r!} />
          <Phones r={r!} />
          <Loose r={r!} />
        </>
      )}
    </main>
  );
}
